import type { PureAbility } from "@casl/ability";
import type { TFormMethods } from "@fluidattacks/design";
import { CardWithInput } from "@fluidattacks/design";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import type { IGroupInfoInput, TInputType } from "../types";
import { Language, ManagedType } from "gql/graphql";

const Cards = ({
  control,
  formState,
  permissions,
  register,
  setValue,
  watch,
}: Readonly<{
  control: TFormMethods<IGroupInfoInput>["control"];
  formState: TFormMethods<IGroupInfoInput>["formState"];
  register: TFormMethods<IGroupInfoInput>["register"];
  permissions: PureAbility<string>;
  setValue: TFormMethods<IGroupInfoInput>["setValue"];
  watch: TFormMethods<IGroupInfoInput>["watch"];
}>): JSX.Element => {
  const { t } = useTranslation();

  const cardsWithInput = [
    {
      description: t("organization.tabs.groups.newGroup.businessId.text"),
      disabled: permissions.cannot(
        "integrates_api_mutations_update_group_stakeholder_mutate",
      ),
      id: "information-businessId",
      name: "businessId",
      tooltip: t("organization.tabs.groups.newGroup.businessId.tooltip"),
    },
    {
      description: t("organization.tabs.groups.newGroup.businessName.text"),
      disabled: permissions.cannot(
        "integrates_api_mutations_update_group_stakeholder_mutate",
      ),
      id: "information-businessName",
      name: "businessName",
      tooltip: t("organization.tabs.groups.newGroup.businessName.tooltip"),
    },
    {
      description: t("organization.tabs.groups.newGroup.description.text"),
      disabled: permissions.cannot(
        "integrates_api_mutations_update_group_stakeholder_mutate",
      ),
      id: "information-description",
      name: "description",
      tooltip: t("organization.tabs.groups.newGroup.description.tooltip"),
    },
    {
      description: t("organization.tabs.groups.newGroup.sprintDuration.text"),
      disabled: permissions.cannot(
        "integrates_api_mutations_update_group_stakeholder_mutate",
      ),
      id: "information-sprintDuration",
      name: "sprintDuration",
      tooltip: t("organization.tabs.groups.newGroup.sprintDuration.tooltip"),
    },
    {
      description: t("organization.tabs.groups.editGroup.sprintStartDate.text"),
      disabled: permissions.cannot(
        "integrates_api_mutations_update_group_stakeholder_mutate",
      ),
      id: "information-sprintStartDate",
      inputType: "date",
      name: "sprintStartDate",
      tooltip: t("organization.tabs.groups.editGroup.sprintStartDate.tooltip"),
    },
  ];

  const cardsWithSelect = [
    {
      disabled: permissions.cannot(
        "integrates_api_mutations_update_group_stakeholder_mutate",
      ),
      id: "information-language",
      items: [
        {
          name: t("organization.tabs.groups.newGroup.language.EN"),
          value: Language.En,
        },
        {
          name: t("organization.tabs.groups.newGroup.language.ES"),
          value: Language.Es,
        },
      ],
      name: "language",
      required: true,
      title: t("organization.tabs.groups.newGroup.language.text"),
      tooltip: t("organization.tabs.groups.newGroup.language.tooltip"),
    },
    {
      disabled: permissions.cannot(
        "integrates_api_mutations_update_group_managed_mutate",
      ),
      id: "information-managed",
      items: [
        {
          name: t("organization.tabs.groups.newGroup.managed.managed"),
          value: ManagedType.Managed,
        },
        {
          name: t("organization.tabs.groups.newGroup.managed.notManaged"),
          value: ManagedType.NotManaged,
        },
        {
          name: t("organization.tabs.groups.newGroup.managed.underReview"),
          value: ManagedType.UnderReview,
        },
        {
          name: t("organization.tabs.groups.newGroup.managed.trial"),
          value: ManagedType.Trial,
        },
      ],
      name: "managed",
      required: true,
      title: t("organization.tabs.groups.newGroup.managed.text"),
      tooltip: t("organization.tabs.groups.newGroup.managed.tooltip"),
    },
  ];

  const getErrorMessage = (fieldName: string): string | undefined => {
    const fieldError =
      formState.errors[fieldName as keyof typeof formState.errors];

    return typeof fieldError?.message === "string"
      ? fieldError.message
      : undefined;
  };

  return (
    <Fragment>
      {cardsWithInput.map(
        ({
          description,
          disabled,
          id,
          inputType,
          name,
          tooltip,
        }): JSX.Element => {
          return (
            <CardWithInput
              control={control}
              disabled={disabled}
              error={getErrorMessage(name)}
              id={id}
              inputType={inputType as TInputType}
              key={name}
              name={name}
              register={register}
              setValue={setValue}
              title={description}
              tooltip={tooltip}
              watch={watch}
            />
          );
        },
      )}
      {cardsWithSelect.map(
        ({ disabled, id, title, name, items, tooltip }): JSX.Element => (
          <CardWithInput
            control={control}
            disabled={disabled}
            id={id}
            inputType={"select"}
            items={items}
            key={name}
            minHeight={"auto"}
            minWidth={"auto"}
            name={name}
            register={register}
            setValue={setValue}
            title={title}
            tooltip={tooltip}
            watch={watch}
          />
        ),
      )}
    </Fragment>
  );
};

export { Cards };
